/** Hook for retrieving the dimensions of any text inside an SVG element */
import type { Dimensions } from '@festina-life/core'
import { type Ref, type SVGAttributes, useLayoutEffect, useState } from 'react'

type Props = {
  text?: string
  fontWeight?: SVGAttributes<SVGTextElement>['fontWeight']
}

/**
 * Hook used for calculating the size of any text inside an SVG component. If no text is provided it will calculate the
 * dimensions of string 'test', which can be useful for calculating the height of any string when the actual string is not
 * known beforehand.
 */
export const useSvgTextSize = <T extends Element>({
  text = 'default',
  fontWeight,
}: Props): { dimensions: Dimensions; svgRef: Ref<T> } => {
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: 0,
    height: 0,
  })
  const [parentRef, setParentRef] = useState<T | null>()

  useLayoutEffect(() => {
    if (parentRef) {
      const svgNS = 'http://www.w3.org/2000/svg'
      const newText = document.createElementNS(svgNS, 'text')

      if (typeof fontWeight === 'string') {
        newText.setAttribute('font-weight', fontWeight)
      }

      const textNode = document.createTextNode(text)
      newText.appendChild(textNode)

      parentRef.appendChild(newText)

      const { width, height } = newText.getBoundingClientRect()

      setDimensions({ width, height })

      parentRef.removeChild(newText)
    }
  }, [parentRef, text, fontWeight])

  return { dimensions, svgRef: setParentRef }
}
